import { MuiAlert, Skeleton, tryParseJSONObject, Typography, useDebounce, useRenderer } from '@fox/atoms';
import { IBlockDefinition, IBlockProps } from '@fox/blocks';
import { useQuery } from '@tanstack/react-query';
import { useSubscribeTopicWithHandler } from 'fox-extensions';
import { POINTS_BLOCK_TYPE } from '../../../form-block-types';
import { getAttributeValues } from '../../../utils';
import { FORM_QUESTION_ANSWERED_TOPIC, useFormRuntimeContext } from '../../form-runtime-context';
import { PointsBlockMetadata, PointsBlockProps, PointsBlockRuntimeProps } from './contracts';

export function PointsBlock({ props, debouncePointsCalculation, getPointsTotal }: IBlockProps<PointsBlockProps> & PointsBlockRuntimeProps) {
	const { top: paddingTop, bottom: paddingBottom, right: paddingRight, left: paddingLeft } = getAttributeValues(props.attributes.padding);
	const { top: marginTop, bottom: marginBottom, right: marginRight, left: marginLeft } = getAttributeValues(props.attributes.margin);
	const fieldMeta = tryParseJSONObject<PointsBlockMetadata>(props.metadataJson, {});
	const { sections, layout, getAnswers } = useFormRuntimeContext();
	const debounceTime = debouncePointsCalculation ? 1000 : 0;
	const pointsRequired = fieldMeta.totalPointsRequired;
	const answers = getAnswers();
	const render = useRenderer();
	useSubscribeTopicWithHandler(FORM_QUESTION_ANSWERED_TOPIC, () => {
		render();
	});
	const debouncedAnswers = useDebounce(answers ?? [], debounceTime); //not sure this will work as hoped?

	const { data, isLoading, isError } = useQuery({
		queryKey: ['pointsTotal', debouncedAnswers],
		queryFn: () => getPointsTotal({ sections, layout, answers: debouncedAnswers }),
		enabled: !!debouncedAnswers,
		keepPreviousData: true,
		staleTime: debounceTime,
		refetchOnWindowFocus: false
	});

	const error = isError || (data && data.status !== 'success');
	return (
		<div
			style={{
				paddingTop: paddingTop,
				paddingRight: paddingRight,
				paddingBottom: paddingBottom,
				paddingLeft: paddingLeft,
				marginTop: marginTop,
				marginRight: marginRight,
				marginBottom: marginBottom,
				marginLeft: marginLeft,
				minHeight: 30
			}}
		>
			<Typography variant="h4" textAlign={'center'}>
				{fieldMeta.label}
			</Typography>
			{error ? (
				<MuiAlert severity="error">Unable to calculate point total</MuiAlert>
			) : (
				<Typography variant="h4" textAlign={'center'}>
					{isLoading ? <Skeleton width={'3rem'} style={{ margin: 'auto' }} /> : data.pointTotal}

					{pointsRequired ? `/${pointsRequired}` : ''}
				</Typography>
			)}
		</div>
	);
}

export const PointsBlockRunTime: IBlockDefinition<PointsBlockProps> = {
	type: POINTS_BLOCK_TYPE,
	Component: pp => {
		const { getPointsTotal } = useFormRuntimeContext();

		return <PointsBlock {...pp} getPointsTotal={getPointsTotal} debouncePointsCalculation={true} />;
	}
};
